import { Box, CircularProgress, PrimaryButton, Typography } from "@crayon/design-system-react";
import { PsaStateMigrationStatus } from "api/client.generated";
import { useNotificationContext } from "context/notificationContext";
import usePsaStateMigrationStatus from "hooks/api/PartnerMigration/usePsaStateMigrationStatus";
import useApi from "hooks/api/useApi";
import { useEffect, useState } from "react";
import NotificationMessage from "types/notification-message";
import MigratePartnerStepProps from "../MigratePartnerStepProps";

const PSAsUsageStep = ({ onStepComplete }: MigratePartnerStepProps) => {
  const { partnersMigrationClient } = useApi();
  const { psaMigrationStatus } = usePsaStateMigrationStatus();

  const [migrationStatus, setMigrationStatus] = useState<PsaStateMigrationStatus | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const [isUpdateBtnVisible, setIsUpdateBtnVisible] = useState(false);
  const [isProgressVisible, setIsProgressVisible] = useState(true);
  const [messageText, setMessageText] = useState("Loading. Please wait...");

  const { raiseErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (!psaMigrationStatus) return;
    setMigrationStatus(psaMigrationStatus);
  }, [psaMigrationStatus]);

  useEffect(() => {
    switch (migrationStatus) {
      case PsaStateMigrationStatus.Completed:
        onStepComplete();
        break;

      case PsaStateMigrationStatus.NotStarted:
        setIsProgressVisible(false);
        setMessageText("Click the 'Update' button to start the process. It might take some time");
        setIsUpdateBtnVisible(true);
        break;

      case PsaStateMigrationStatus.InProgress:
        setIsProgressVisible(true);
        setMessageText("Update is in progress. Please check again later");
        setIsUpdateBtnVisible(false);
        break;

      default:
        break;
    }
  }, [migrationStatus, onStepComplete]);

  const onUpdate = async () => {
    try {
      setIsUpdating(true);
      const status = await partnersMigrationClient.migratePsaState();
      setMigrationStatus(status);
    } catch {
      raiseErrorNotification(NotificationMessage.UNKNOWN_ERROR);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap={1}
      mt={2}
    >
      {isProgressVisible && <CircularProgress />}
      <Typography variant="body1">{messageText}</Typography>
      {isUpdateBtnVisible && (
        <PrimaryButton onClick={onUpdate} loading={isUpdating} testId="update-button">
          Update
        </PrimaryButton>
      )}
    </Box>
  );
};

export default PSAsUsageStep;
